<script setup>
  import HomeButton from '@/components/home/HomeButton.vue';
  import HomeSectionHeading from '@/components/home/HomeSectionHeading.vue';

  import { HOME_WHY } from '@/constants/home';

  import houseIcon from '@/assets/images/home/icon-section-house.svg';

  defineEmits(['get-price']);
</script>

<template>
  <section id="why" class="relative w-full scroll-mt-14 bg-white md:scroll-mt-30 lg:scroll-mt-43">
    <!-- overlaps by 1px to hide a sub-pixel seam at fractional zoom -->
    <div
      class="absolute inset-x-0 bottom-[calc(100%-1px)] aspect-1518/96 bg-white [clip-path:polygon(0_100%,50%_0,100%_100%)]"
      aria-hidden="true"
    />

    <div
      class="relative mx-auto flex w-full max-w-299 flex-col items-center px-5 pb-32 md:px-8 xl:px-0 xl:pb-36"
    >
      <img :src="houseIcon" alt="" class="mt-8 size-10 md:mt-0 md:size-12 lg:-mt-11.5 lg:size-13" />

      <HomeSectionHeading
        v-reveal
        class="mt-8 lg:mt-13"
        :eyebrow="HOME_WHY.eyebrow"
        :title="HOME_WHY.title"
      />

      <div
        v-reveal="100"
        class="text-ink mt-3 max-w-90 text-center text-[13px]/4.5 font-medium sm:max-w-120 sm:text-[15px]/5.5 md:mt-6 md:max-w-180 md:text-[16px]/6 lg:mt-9 lg:max-w-223 lg:text-[18px]/6.5"
      >
        <p>{{ HOME_WHY.intro }}</p>
        <p class="mt-4 md:mt-6 lg:mt-6.5">
          {{ HOME_WHY.closing[0] }}<br class="hidden lg:inline" />
          {{ HOME_WHY.closing[1] }}
        </p>
      </div>

      <div
        class="mt-12 grid w-full max-w-126 gap-10 px-3 lg:mt-14 lg:max-w-none lg:grid-cols-2 lg:px-0 xl:mt-15 xl:gap-23"
      >
        <!-- thicker border, so less padding: both cards keep their content on the same lines -->
        <div
          v-for="card in HOME_WHY.comparisons"
          :key="card.title"
          v-reveal="card.highlighted ? 150 : 0"
          :class="[
            'rounded-[20px]',
            card.highlighted
              ? 'running-border border-6 border-transparent px-7.5 py-11.5 lg:px-9.5 xl:border-8 xl:pt-13 xl:pr-11 xl:pb-14 xl:pl-14'
              : 'border-platinum border-4 border-dashed px-8 py-12 lg:px-10 xl:px-13 xl:pt-14 xl:pb-15',
          ]"
        >
          <!-- offset as in the design (22px at 32px) -->
          <h3
            :class="[
              'text-[24px] leading-[1.22] font-bold md:text-[28px] xl:text-[32px]',
              card.highlighted ? 'text-cobalt ml-[0.69em]' : 'text-ink',
            ]"
          >
            {{ card.title }}
          </h3>

          <ul
            class="text-ink mt-7 flex flex-col gap-3 pl-[0.67em] text-[15px] leading-[1.22] font-medium tracking-[-0.005em] md:text-[16px] lg:text-[20px] xl:mt-7.5 xl:text-[24px]"
          >
            <li
              v-for="(step, index) in card.steps"
              :key="step.text"
              class="reveal-item flex gap-[0.5em] before:content-['•']"
              :style="{ '--item-delay': `calc(var(--reveal-delay, 0ms) + ${300 + index * 80}ms)` }"
              :class="[step.strong && 'font-bold', step.strong && card.highlighted && 'uppercase']"
            >
              {{ step.text }}
            </li>
          </ul>
        </div>
      </div>

      <HomeButton class="mt-12 md:mt-16 xl:mt-21" @click="$emit('get-price')">
        {{ HOME_WHY.cta }}
      </HomeButton>
    </div>
  </section>
</template>

<style scoped>
  /* list items follow their v-reveal parent in one by one */
  .reveal:not(.is-revealed) .reveal-item {
    opacity: 0;
  }

  .is-revealed .reveal-item {
    animation: reveal-item-in 0.4s ease-out var(--item-delay) both;
  }

  @keyframes reveal-item-in {
    from {
      opacity: 0;
      transform: translateX(-12px);
    }
  }

  /* registered so the angle can be animated */
  @property --border-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  .running-border {
    /* static border where @property is unsupported */
    --border-angle: 0deg;

    background:
      linear-gradient(#fff, #fff) padding-box,
      conic-gradient(
          from var(--border-angle),
          var(--color-blue-light),
          var(--color-bright-cyan),
          var(--color-blue-light)
        )
        border-box;
    animation: border-run 4s linear infinite;
  }

  @keyframes border-run {
    to {
      --border-angle: 360deg;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .running-border {
      animation: none;
    }
  }
</style>
